import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCheck } from 'lucide-react';
import { PageHeader } from '@kioskgaming/ui';
import { apiClient } from '../../../services/api';
import type { AdminNotificationRow } from '../../AdminNotificationBell';
import { useTimezone, formatInTimezone } from '@kioskgaming/ui';
import { Button } from '@/components/ui/shadcn';
import { useAuth } from '../../../hooks/useAuth';

const CATEGORIES = ['finance', 'operations', 'support', 'security', 'announcement'] as const;
const SEVERITIES = ['info', 'warning', 'critical'] as const;

type CategoryFilter = (typeof CATEGORIES)[number] | '';
type SeverityFilter = (typeof SEVERITIES)[number] | '';

function severityBadgeClass(severity: AdminNotificationRow['severity']): string {
  switch (severity) {
    case 'critical':
      return 'bg-destructive/10 text-destructive';
    case 'warning':
      return 'bg-amber-500/10 text-amber-700 dark:text-amber-400';
    default:
      return 'bg-primary/10 text-primary';
  }
}

function formatTime(iso: string, timezone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return formatInTimezone(date, timezone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function resolveNavigatePath(actionUrl: string | null): string | null {
  if (!actionUrl) return null;
  if (actionUrl.startsWith('/')) return actionUrl;
  try {
    const url = new URL(actionUrl);
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { timezone } = useTimezone();
  const { hasRole } = useAuth();
  const canSendAnnouncement = hasRole(['super_admin', 'system_admin']);
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementBody, setAnnouncementBody] = useState('');
  const [announcementSending, setAnnouncementSending] = useState(false);
  const [items, setItems] = useState<AdminNotificationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [category, setCategory] = useState<CategoryFilter>('');
  const [severity, setSeverity] = useState<SeverityFilter>('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const loadPage = useCallback(
    async (cursor: string | null, append: boolean) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const res = await apiClient.getAdminNotificationsAll({
          cursor: cursor ?? undefined,
          category: category || undefined,
          severity: severity || undefined,
          unreadOnly,
          limit: 30,
        });
        if (res.success && res.data) {
          const rows = res.data.notifications ?? [];
          setItems((prev) => (append ? [...prev, ...rows] : rows));
          setNextCursor(res.data.nextCursor ?? null);
          setHasMore(Boolean(res.data.hasMore));
        } else if (!append) {
          setItems([]);
        }
      } catch {
        if (!append) setItems([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [category, severity, unreadOnly],
  );

  useEffect(() => {
    void loadPage(null, false);
  }, [loadPage]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && nextCursor && !loadingMore) {
          void loadPage(nextCursor, true);
        }
      },
      { rootMargin: '120px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, nextCursor, loadingMore, loadPage]);

  const handleAckAll = useCallback(async () => {
    try {
      await apiClient.ackAllAdminNotifications({
        category: category || undefined,
        severity: severity || undefined,
      });
      void loadPage(null, false);
    } catch {
      // noop
    }
  }, [category, severity, loadPage]);

  const handleSendAnnouncement = useCallback(async () => {
    if (!announcementTitle.trim() || !announcementBody.trim()) return;
    setAnnouncementSending(true);
    try {
      await apiClient.createAdminAnnouncement({
        title: announcementTitle.trim(),
        body: announcementBody.trim(),
      });
      setAnnouncementTitle('');
      setAnnouncementBody('');
      void loadPage(null, false);
    } catch {
      // noop
    } finally {
      setAnnouncementSending(false);
    }
  }, [announcementTitle, announcementBody, loadPage]);

  const handleItemClick = useCallback(
    async (item: AdminNotificationRow) => {
      if (!item.acknowledged) {
        try {
          await apiClient.ackAdminNotification(item.id);
          setItems((prev) =>
            prev.map((n) => (n.id === item.id ? { ...n, acknowledged: true } : n)),
          );
        } catch {
          // noop
        }
      }
      const path = resolveNavigatePath(item.actionUrl);
      if (path) navigate(path);
    },
    [navigate],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        description="Actionable alerts for your role. Routine activity stays in transaction and cashflow logs."
      />

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-4">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Category
          <select
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
            value={category}
            onChange={(e) => setCategory(e.target.value as CategoryFilter)}
          >
            <option value="">All</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Severity
          <select
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as SeverityFilter)}
          >
            <option value="">All</option>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 pb-1.5 text-sm text-foreground">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
            className="rounded border-border"
          />
          Unread only
        </label>
        <Button type="button" variant="outline" size="sm" className="ml-auto gap-1.5" onClick={() => void handleAckAll()}>
          <CheckCheck className="h-4 w-4" />
          Mark all read
        </Button>
      </div>

      {canSendAnnouncement ? (
        <div className="space-y-3 rounded-lg border border-border bg-card p-4">
          <p className="text-sm font-medium text-foreground">Send announcement to all admins</p>
          <input
            type="text"
            value={announcementTitle}
            onChange={(e) => setAnnouncementTitle(e.target.value)}
            placeholder="Title"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            maxLength={500}
          />
          <textarea
            value={announcementBody}
            onChange={(e) => setAnnouncementBody(e.target.value)}
            placeholder="Message body"
            rows={3}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <Button
            type="button"
            size="sm"
            disabled={announcementSending || !announcementTitle.trim() || !announcementBody.trim()}
            onClick={() => void handleSendAnnouncement()}
          >
            {announcementSending ? 'Sending…' : 'Send announcement'}
          </Button>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {loading && items.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-muted-foreground">No notifications match your filters.</p>
        ) : (
          <ul>
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="flex w-full items-start gap-3 border-b border-border px-4 py-4 text-left transition last:border-b-0 hover:bg-muted/40"
                  style={{ opacity: item.acknowledged ? 0.6 : 1 }}
                  onClick={() => void handleItemClick(item)}
                >
                  <span className="mt-1 min-w-[4.5rem]">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-[10px] font-semibold uppercase ${severityBadgeClass(item.severity)}`}
                    >
                      {item.severity}
                    </span>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="mb-1 flex flex-wrap items-center gap-2">
                      <span className="font-medium text-foreground">{item.title}</span>
                      {item.isAnnouncement ? (
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
                          Announcement
                        </span>
                      ) : null}
                      <span className="text-[11px] capitalize text-muted-foreground">{item.category}</span>
                    </span>
                    <p className="mb-1 text-sm text-muted-foreground">{item.body}</p>
                    <p className="text-xs text-muted-foreground/80">{formatTime(item.createdAt, timezone)}</p>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {loadingMore ? (
          <p className="px-4 py-4 text-center text-sm text-muted-foreground">Loading more…</p>
        ) : null}
        <div ref={sentinelRef} className="h-1" aria-hidden />
      </div>
    </div>
  );
}
